export const allowedCountries = [
    'DE',
    'AT',
    'CH',
    'PL',
    'ES',
    'IT',
    'FR',
    'EN',
    'UK',
    'GB',
    'NL',
    'CA',
    'HK',
    'TR',
    'SE',
    'NO',
    'CZ',
    'SI',
    'HU',
    'RO',
    'CO'
] as const;

export type AllowedCountries = (typeof allowedCountries)[number];

// Language definitions - single source of truth for all language metadata
export const LANGUAGE_DEFINITIONS = {
    en: { name: 'English', flag: 'gb' },
    de: { name: 'Deutsch' },
    es: { name: 'Español' },
    fr: { name: 'Français' },
    it: { name: 'Italiano' },
    nl: { name: 'Nederlands' },
    pl: { name: 'Polski' },
    sv: { name: 'Svenska', flag: 'se' },
    tr: { name: 'Türkçe' },
    cs: { name: 'Čeština', flag: 'cz' },
    sl: { name: 'Slovenščina', flag: 'si' },
    hu: { name: 'Magyar' },
    ro: { name: 'Română' },
    nb: { name: 'Norsk', flag: 'no' }
} as const;

export const LANGUAGE_PREFERENCE_KEY = 'ml-try-preferred-language';

export type LanguageCode = keyof typeof LANGUAGE_DEFINITIONS;

export type LanguageDefinition = {
    name: string;
    flag?: string;
};

export const getLanguageFlag = (code: LanguageCode): string => {
    const lang = LANGUAGE_DEFINITIONS[code];
    return (lang && 'flag' in lang ? lang.flag : undefined) || code;
};

export const localesPathMappings: Record<AllowedCountries, LanguageCode> = {
    DE: 'de',
    AT: 'de',
    CH: 'de',
    EN: 'en',
    UK: 'en',
    GB: 'en',
    CA: 'en',
    HK: 'en',
    IT: 'it',
    PL: 'pl',
    SE: 'sv',
    TR: 'tr',
    ES: 'es',
    CO: 'es',
    FR: 'fr',
    NL: 'nl',
    CZ: 'cs',
    SI: 'sl',
    HU: 'hu',
    RO: 'ro',
    NO: 'nb'
};

export const allowedStudioLocations = [
    'DE',
    'AT',
    'CH',
    'FR',
    'ES',
    'IT',
    'GB',
    'NL'
];

const specialMappings: Record<string, AllowedCountries> = {
    EN: 'GB',
    UK: 'GB'
};

export const considerSpecialCountriesMappings = (country: AllowedCountries) => {
    return specialMappings[country] || country;
};

export interface UserLocale {
    lang: LanguageCode;
    countryCode: AllowedCountries;
}

export const isCountryAllowed = (
    country?: string
): country is AllowedCountries => {
    if (!country || country === '') {
        return false;
    }
    return allowedCountries.includes(country as AllowedCountries);
};

export const defaultLocale: UserLocale = { lang: 'de', countryCode: 'DE' };

const getRelatedCountry = (localeCode?: string) => {
    if (!localeCode) {
        return undefined;
    }
    const upperCode = localeCode.toUpperCase();
    if (!isCountryAllowed(upperCode)) {
        return undefined;
    }

    return considerSpecialCountriesMappings(upperCode);
};

export const getLocaleFromBrowser = () => {
    const [lang, countryCode] = navigator.language.split('-');

    const isValidLanguageCode = (code?: string): code is LanguageCode => {
        return !!code && code in LANGUAGE_DEFINITIONS;
    };

    const savedLanguage = localStorage.getItem(LANGUAGE_PREFERENCE_KEY) ?? '';

    const country = getRelatedCountry(countryCode);
    if (country) {
        return {
            lang: isValidLanguageCode(savedLanguage)
                ? savedLanguage
                : localesPathMappings[country],
            countryCode: country
        };
    }

    // Note: This handles cases when the countryCode extracted from the browser is not in the allowed countries list, like: "en-US"
    const selectedCountryByLang = getRelatedCountry(lang);
    if (selectedCountryByLang) {
        return {
            lang: isValidLanguageCode(savedLanguage)
                ? savedLanguage
                : localesPathMappings[selectedCountryByLang],
            countryCode: selectedCountryByLang
        };
    }
    return defaultLocale;
};
